import {lazy, Suspense, useRef} from "react";
import {AnimatePresence} from "motion/react";
import {ScrollArea} from "../../components/ui";
import {AuthModal} from "../../components/modals/AuthModal";
import {useLandingHelpers} from "../../hooks/useLandingHelpers";

import {Header, HeroSection, Footer, usePressFeedback} from "../../components/Landing";
import {landingCssVars} from "../../components/Landing/motion";

// Ниже первого экрана — отдельным чанком: шапка и герой должны рисоваться сразу.
const HowItWorksSection = lazy(() => import("../../components/Landing/Sections/HowItWorksSection").then(m => ({default: m.HowItWorksSection})));
const CompatibilitySection = lazy(() => import("../../components/Landing/Sections/CompatibilitySection").then(m => ({default: m.CompatibilitySection})));
const QrSection = lazy(() => import("../../components/Landing/Sections/QrSection").then(m => ({default: m.QrSection})));
const PlacesSection = lazy(() => import("../../components/Landing/Sections/PlacesSection").then(m => ({default: m.PlacesSection})));
const PricingSection = lazy(() => import("../../components/Landing/Sections/PricingSection").then(m => ({default: m.PricingSection})));
const TrustSection = lazy(() => import("../../components/Landing/Sections/TrustSection").then(m => ({default: m.TrustSection})));
// Прямой импорт секции, а не через barrel components/Landing/index.ts: barrel
// реэкспортирует все секции и Header/Footer, между модулями возникает
// циркулярная зависимость чанков (Rollup предупреждает в билде), и на холодной
// загрузке namespace barrel бывает undefined — «e.FinalCtaSection» крашил
// лендинг при первом запуске (issue #667, п.1/2).
const FinalCtaSection = lazy(() => import("../../components/Landing/Sections/FinalCtaSection").then(m => ({default: m.FinalCtaSection})));

export function LandingPage() {
    const {
        openRegister,
        openLogin,
        showAuth,
        closeAuth,
        authInitialMode,
        authNotice,
        resetToken,
        entryMode,
        handleAuthSuccess
    } = useLandingHelpers();

    // Отклик на нажатие для всей страницы — один обработчик на корне лендинга.
    // Почему не CSS `:active` (он тут был и на телефоне не работал) — см.
    // `components/Landing/usePressFeedback.ts`.
    const landingRef = useRef<HTMLDivElement | null>(null);
    usePressFeedback(landingRef);

    return (
        /* Шапка — СЕСТРА ScrollArea, а не внутри него: лендинг скроллится в кастомном
           ScrollArea (OverlayScrollbars), где position: sticky на .rl-header не работает
           (см. StickyCtaBar). Поэтому шапку держим снаружи скролла в flex-колонке —
           так она всегда видна при прокрутке вниз (Ирина, 08.09). */
        <div className="rl-landing-shell flex h-dvh min-h-dvh flex-col bg-[var(--rl-bg)]" style={landingCssVars}>
            <Header
                openLogin={openLogin}
            />
            <ScrollArea overflowX="hidden" className="flex-1 min-h-0 w-full">
            {/* rl-landing — корень визуального языка лендинга (styles/landing.css).
                Счётчик Яндекс.Метрики живёт в web/index.html и от разметки страницы
                не зависит — переверстка секций его не задевает. */}
            <div className="rl-landing" ref={landingRef}>
                <HeroSection
                    openRegister={openRegister}
                />
                <Suspense fallback={<div className="py-24 text-center text-gray-500">Загрузка...</div>}>
                    <HowItWorksSection/>
                    <CompatibilitySection/>
                    <QrSection/>
                    <PlacesSection/>
                    <PricingSection openRegister={openRegister}/>
                    <TrustSection/>
                    <FinalCtaSection
                        openRegister={openRegister}
                    />
                    <Footer
                        openRegister={openRegister}
                        openLogin={openLogin}
                    />
                </Suspense>
            </div>

            {/* Auth Modal */}
            <AnimatePresence>
                {showAuth && (
                    <AuthModal
                        initialMode={authInitialMode}
                        notice={authNotice ?? undefined}
                        resetToken={resetToken}
                        entryMode={entryMode}
                        onClose={closeAuth}
                        onSuccess={handleAuthSuccess}
                    />
                )}
            </AnimatePresence>
            </ScrollArea>
        </div>
    );
}
